import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import CanonicalTags from "@/components/seo/CanonicalTags";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { CtaBand, SignUpCta } from "@/components/marketing/parts";
import { coverage } from "@/content/coverage";
import { featuredRelease, formatPressDate, listedReleases, type PressRelease } from "@/content/press";

/**
 * Newsroom (/press). Releases come from the markdown loader in
 * src/content/press (drafts excluded, newest first, one featured lead) and
 * coverage from src/content/coverage.ts, so a new release or article needs no
 * component edit. Published text is rendered as authored.
 */

/** Links a release to /press/:slug, or out to `externalUrl` when one is set. */
const ReleaseLink = ({
  release,
  className,
  children,
}: {
  release: PressRelease;
  className?: string;
  children: React.ReactNode;
}) =>
  release.externalUrl ? (
    <a href={release.externalUrl} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  ) : (
    <Link to={`/press/${release.slug}`} className={className}>
      {children}
    </Link>
  );

const EmptyNote = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="mk-stack" style={{ gap: 8, padding: "28px 0", borderBottom: "1px solid var(--mk-border)" }}>
    <span style={{ font: "600 var(--mk-text-h3-sm)/1.2 var(--mk-sans)" }}>{title}</span>
    <p className="mk-body">{children}</p>
  </div>
);

/**
 * Brand colors, in the order shown. `token` is the live .mk custom property
 * (fills come from it); `hex` and the ratios are printed text. Ratios are the
 * WCAG 2.x values in docs/design_handoff_press_v3.1/Press Handoff Notes.md §1.
 */
const HEX_STYLE = { fontWeight: 500, color: "var(--mk-ink)" } as const;
const TONE_COLOR: Record<string, string> = { pass: "var(--mk-primary)", fail: "var(--mk-error)", ref: "var(--mk-muted)" };
const PASS = { label: "pass · AA 4.5:1", tone: "pass" } as const;
const REFERENCE = { label: "reference only", tone: "ref" } as const;
const FAIL = { label: "fail · 3:1", tone: "fail" } as const;
const PAPER = "#F7F7F4";
const WHITE = "#FFFFFF";

interface ColorSwatch {
  name: string;
  token: string;
  hex: string;
  kind: "text" | "non-text";
  result: { label: string; tone: string };
  gold?: boolean;
  pairs: { against: string; ratio: string }[];
}

const COLORS: ColorSwatch[] = [
  {
    name: "Primary",
    token: "--mk-primary",
    hex: "#13565A",
    kind: "text",
    result: PASS,
    pairs: [
      { against: PAPER, ratio: "7.81" },
      { against: WHITE, ratio: "8.38" },
    ],
  },
  {
    name: "Ink",
    token: "--mk-ink",
    hex: "#1C1813",
    kind: "text",
    result: PASS,
    pairs: [
      { against: PAPER, ratio: "16.45" },
      { against: WHITE, ratio: "17.66" },
    ],
  },
  {
    name: "Ink 2",
    token: "--mk-ink-2",
    hex: "#4A443B",
    kind: "text",
    result: PASS,
    pairs: [
      { against: PAPER, ratio: "8.97" },
      { against: WHITE, ratio: "9.63" },
    ],
  },
  {
    name: "Muted",
    token: "--mk-muted",
    hex: "#5E574D",
    kind: "text",
    result: PASS,
    pairs: [
      { against: PAPER, ratio: "6.64" },
      { against: WHITE, ratio: "7.13" },
    ],
  },
  {
    name: "Paper",
    token: "--mk-paper",
    hex: PAPER,
    kind: "non-text",
    result: REFERENCE,
    pairs: [
      { against: PAPER, ratio: "1.00" },
      { against: WHITE, ratio: "1.07" },
    ],
  },
  {
    name: "Tint",
    token: "--mk-tint",
    hex: "#E9F0F0",
    kind: "non-text",
    result: REFERENCE,
    pairs: [
      { against: PAPER, ratio: "1.08" },
      { against: WHITE, ratio: "1.15" },
    ],
  },
  {
    name: "Gold",
    token: "--mk-gold",
    hex: "#C99700",
    kind: "non-text",
    result: FAIL,
    gold: true,
    pairs: [
      { against: PAPER, ratio: "2.47" },
      { against: WHITE, ratio: "2.65" },
    ],
  },
];

const listGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,340px),1fr))",
  gap: "40px clamp(48px,7cqi,112px)",
  alignItems: "start",
};

const PressPage = () => (
  <MarketingLayout>
    <CanonicalTags
      path="/press"
      title="Compaire Newsroom | News, Press & Media Resources"
      description="Read Compaire announcements and media coverage. Find press releases, brand assets and media contacts for our real estate comparison platform."
    />

    <section style={{ padding: "clamp(64px,9cqi,128px) 0 clamp(48px,6cqi,80px)" }}>
      <div
        className="mk-container"
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: "20px 64px",
        }}
      >
        <div className="mk-stack" style={{ gap: 24 }}>
          <div className="mk-label">Newsroom</div>
          <h1 className="mk-h1" style={{ font: "700 var(--mk-text-hero)/1 var(--mk-sans)" }}>
            Press &amp; News
          </h1>
        </div>
        <p className="mk-body-lg" style={{ flex: "0 1 420px" }}>
          News, announcements and media coverage from Compaire.
        </p>
      </div>
    </section>

    {featuredRelease && (
      <section style={{ padding: "0 0 clamp(72px,9cqi,120px)" }}>
        <div className="mk-container">
          <article
            className="mk-card"
            style={{
              padding: "clamp(28px,5cqi,64px)",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,420px),1fr))",
              gap: "32px clamp(40px,6cqi,96px)",
              alignItems: "end",
            }}
          >
            <div className="mk-stack" style={{ gap: 20 }}>
              <span style={{ font: "500 var(--mk-text-label-sm)/1.3 var(--mk-sans)", color: "var(--mk-primary)" }}>
                Press Release · Featured
              </span>
              <h2 className="mk-h2">
                {featuredRelease.title}
              </h2>
            </div>
            <div className="mk-stack" style={{ alignItems: "flex-start", gap: 20 }}>
              <span className="mk-sample">
                {[featuredRelease.city, formatPressDate(featuredRelease.date)].filter(Boolean).join(" — ")}
              </span>
              {featuredRelease.summary && (
                <p className="mk-body-lg" style={{ lineHeight: 1.6 }}>
                  {featuredRelease.summary}
                </p>
              )}
              <ReleaseLink release={featuredRelease} className="mk-btn mk-btn--primary">
                Read the release {featuredRelease.externalUrl ? "↗" : "→"}
              </ReleaseLink>
            </div>
          </article>
        </div>
      </section>
    )}

    {(listedReleases.length > 0 || !featuredRelease) && (
      <section className="mk-section mk-section--rule">
        <div className="mk-container" style={listGrid}>
          <div className="mk-stack" style={{ gap: 14 }}>
            <span className="mk-label mk-label--muted" style={{ fontSize: "var(--mk-text-label-sm)" }}>
              Compaire announcements
            </span>
            <h2 className="mk-h2">Press releases</h2>
          </div>
          <div className="mk-newslist">
            {listedReleases.length > 0 ? (
              listedReleases.map((release) => (
                <ReleaseLink key={release.slug} release={release} className="mk-newslist__item">
                  <span className="mk-sample">
                    {[release.city, formatPressDate(release.date)].filter(Boolean).join(" — ")}
                    {release.category ? ` · ${release.category}` : ""}
                  </span>
                  <h3 style={{ font: "600 var(--mk-text-h3)/1.25 var(--mk-sans)" }}>
                    {release.title} {release.externalUrl ? "↗" : ""}
                  </h3>
                  {release.summary && <p>{release.summary}</p>}
                </ReleaseLink>
              ))
            ) : (
              <EmptyNote title="No releases yet">New announcements from Compaire will appear here.</EmptyNote>
            )}
          </div>
        </div>
      </section>
    )}

    <section className="mk-section mk-section--white mk-section--rule">
      <div className="mk-container" style={listGrid}>
        <div className="mk-stack" style={{ gap: 14 }}>
          <span className="mk-label mk-label--muted" style={{ fontSize: "var(--mk-text-label-sm)" }}>
            Company · 2026
          </span>
          <h2 className="mk-h2">In the news</h2>
          <span className="mk-label">Media coverage</span>
        </div>
        <div className="mk-newslist">
          {coverage.length > 0 ? (
            coverage.map((item) => (
              <a
                key={item.url}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mk-newslist__item"
              >
                <span className="mk-sample">
                  {item.outlet} · {formatPressDate(item.date)}
                </span>
                <h3 style={{ font: "600 var(--mk-text-h3)/1.25 var(--mk-sans)" }}>{item.headline} ↗</h3>
                <p>{item.excerpt}</p>
              </a>
            ))
          ) : (
            <EmptyNote title="Coverage coming soon">
              As journalists cover Compaire, features and mentions will be collected here. Working on a story? Reach us
              at <a href="mailto:press@compaire.co">press@compaire.co</a>.
            </EmptyNote>
          )}
        </div>
      </div>
    </section>

    <section className="mk-section">
      <div className="mk-container mk-stack" style={{ gap: 32 }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: "16px 40px",
          }}
        >
          <div className="mk-stack" style={{ gap: 14 }}>
            <span className="mk-label">Media resources</span>
            <h2 className="mk-h2">Press kit</h2>
          </div>
          <a href="/compaire-press-kit-2026-10.zip" download className="mk-btn mk-btn--dark">
            <Download size={18} aria-hidden="true" />
            Download press kit →
          </a>
        </div>
        <div className="mk-card mk-pr-card">
          <h3 className="mk-h2">Wordmark</h3>
          <p className="mk-body-lg" style={{ marginTop: 12, maxWidth: 640 }}>
            The gold center dot of the icon is the "pair." Never recolor it, and
            keep clear space equal to the icon's height on all sides.
          </p>
          <div className="mk-pr-logos">
            <article className="mk-card mk-pr-lg">
              <div
                className="mk-pr-stage"
                style={{ background: "var(--mk-paper)" }}
              >
                <img
                  src="/compaire-logo.svg"
                  alt="Compaire primary logo on paper"
                  width={420}
                  height={39}
                />
              </div>
              <div className="mk-pr-tb">
                <h4 className="mk-h3-sm">Primary logo</h4>
                <div className="mk-meta">
                  On paper <span style={HEX_STYLE}>#F7F7F4</span> or
                  white
                </div>
                <div className="mk-pr-dls">
                  <a
                    href="/compaire-logo.svg"
                    download
                    className="mk-btn mk-btn--quiet mk-btn--sm"
                  >
                    <Download size={18} aria-hidden="true" />
                    SVG
                  </a>
                  <a
                    href="/compaire-logo.png"
                    download
                    className="mk-btn mk-btn--quiet mk-btn--sm"
                  >
                    <Download size={18} aria-hidden="true" />
                    PNG
                  </a>
                </div>
              </div>
            </article>
            <article className="mk-card mk-pr-lg">
              <div
                className="mk-pr-stage"
                style={{ background: "var(--mk-primary)" }}
              >
                <img
                  src="/compaire-logo-white.png"
                  alt="Compaire white logo on teal"
                  width={420}
                  height={39}
                />
              </div>
              <div className="mk-pr-tb">
                <h4 className="mk-h3-sm">White logo</h4>
                <div className="mk-meta">
                  On teal <span style={HEX_STYLE}>#13565A</span>
                </div>
                <div className="mk-pr-dls">
                  <a
                    href="/compaire-logo-white.png"
                    download
                    className="mk-btn mk-btn--quiet mk-btn--sm"
                  >
                    <Download size={18} aria-hidden="true" />
                    PNG
                  </a>
                </div>
              </div>
            </article>
          </div>
          <article className="mk-card mk-pr-lg mk-pr-cs">
            <div className="mk-pr-cs-fig">
              <div className="mk-pr-dia">
                <div className="mk-pr-ring" />
                <div className="mk-pr-art">
                  <img src="/compaire-logo.svg" alt="" />
                </div>
                <span className="mk-pr-dm mk-pr-dm-t" aria-hidden="true">
                  <i />
                  <b>
                    <span className="mk-pr-d">x · 37.4px</span>
                    <span className="mk-pr-mo">x · 19.2px</span>
                  </b>
                </span>
                <span className="mk-pr-dm mk-pr-dm-b" aria-hidden="true">
                  <i />
                  <b>x</b>
                </span>
                <span className="mk-pr-dm mk-pr-dm-l" aria-hidden="true">
                  <i />
                  <b>x</b>
                </span>
                <span className="mk-pr-dm mk-pr-dm-r" aria-hidden="true">
                  <i />
                  <b>x</b>
                </span>
              </div>
            </div>
            <div className="mk-pr-tb mk-pr-cs-txt">
              <h4 className="mk-h3-sm">Clear space</h4>
              <p className="mk-body">
                Keep <b className="mk-meta" style={HEX_STYLE}>
                  x
                </b> clear on all
                sides, where <b className="mk-meta" style={HEX_STYLE}>
                  x
                </b> is the
                height of the icon mark.
              </p>
              <div className="mk-meta">
                x = icon mark height
                <br />
                528 / 6500 = 0.0812 × artwork width
                <br />
                <span className="mk-pr-d">460px wide → x = 37.4px</span>
                <span className="mk-pr-mo">236px wide → x = 19.2px</span>
              </div>
              <p className="mk-small">
                Measured from the edge of the artwork file. Minimum width: 132px
                on screen (the site header's smallest size).
              </p>
            </div>
          </article>
        </div>
        <div className="mk-card mk-pr-card">
          <h3 className="mk-h2">Brand colors</h3>
          <div className="mk-pr-tiles">
            {COLORS.map((c) => (
              <article
                key={c.token}
                className={`mk-card mk-pr-tile${c.gold ? " mk-pr-tile--gold" : ""}`}
              >
                <div
                  className="mk-pr-sw"
                  style={{ background: `var(${c.token})` }}
                  aria-hidden="true"
                />
                <div className="mk-pr-tb">
                  <h4 className="mk-h3-sm">{c.name}</h4>
                  <div className="mk-meta">{c.token}</div>
                  <div className="mk-meta" style={HEX_STYLE}>
                    {c.hex}
                  </div>
                  <div className="mk-pr-crs">
                    {c.pairs.map((pair) => (
                      <div key={pair.against} className="mk-pr-cr">
                        <div className="mk-pr-cr-row">
                          <span className="mk-meta">vs {pair.against}</span>
                          <span className="mk-tag mk-tag--mono">
                            {c.kind === "text" ? "Text" : "Non-text"}
                          </span>
                        </div>
                        <div className="mk-pr-cr-row">
                          <span className="mk-data mk-data--cell">{pair.ratio}:1</span>
                          <span className="mk-meta" style={{ color: TONE_COLOR[c.result.tone] }}>
                            {c.result.label}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {c.gold && (
                  <div className="mk-pr-gold-note">
                    <div
                      className="mk-label-sm"
                      style={{ color: "var(--mk-ink)" }}
                    >
                      Non-text only · Top Match marker and 2px rules
                    </div>
                    <div className="mk-pr-gold-demo">
                      <span className="mk-tag mk-tag--top">Top Match</span>
                      <span className="mk-rule-gold" />
                    </div>
                    <p
                      className="mk-small"
                      style={{ color: "var(--mk-ink-2)" }}
                    >
                      Gold fails the 3:1 non-text minimum on both backgrounds —
                      2.47:1 on paper, 2.65:1 on white. It is decorative: the
                      “Top Match” label carries the meaning, so nothing relies
                      on the gold alone. Never text, and never the fill of a
                      button, chip or panel.
                    </p>
                  </div>
                )}
              </article>
            ))}
          </div>
          <p className="mk-meta" style={{ marginTop: 20 }}>
            Ratios: WCAG 2.x relative luminance, computed. Text pairing needs
            4.5:1; non-text pairing needs 3:1.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,340px),1fr))", gap: 16 }}>
          <div className="mk-card" style={{ padding: 28, display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
            <img
              src="/press/jesse-garcia.jpg"
              alt="Jesse Garcia, Founder and CEO of Compaire"
              width={120}
              height={120}
              loading="lazy"
              style={{ width: 120, height: "auto", borderRadius: 6, display: "block", background: "var(--mk-tint)" }}
            />
            <div className="mk-stack" style={{ flex: "1 1 180px", gap: 8 }}>
              <span style={{ font: "500 var(--mk-text-label-sm)/1.3 var(--mk-sans)", color: "var(--mk-primary)" }}>Founder</span>
              <h3 style={{ font: "600 var(--mk-text-h3)/1.25 var(--mk-sans)" }}>Jesse Garcia</h3>
              <span style={{ font: "400 var(--mk-text-small)/1.4 var(--mk-sans)", color: "var(--mk-ink-2)" }}>
                Founder &amp; CEO, Compaire
              </span>
              <p style={{ margin: "8px 0 0", font: "400 var(--mk-text-body)/1.6 var(--mk-sans)", color: "var(--mk-muted)" }}>
                Bio and high-resolution headshot available on request.
              </p>
            </div>
          </div>
          <div className="mk-card mk-stack" style={{ padding: 28, gap: 20 }}>
            <h3 style={{ font: "600 var(--mk-text-h3-sm)/1.2 var(--mk-sans)" }}>Product imagery</h3>
            <div
              style={{
                display: "flex",
                gap: 20,
                alignItems: "center",
                padding: 20,
                background: "var(--mk-paper)",
                borderRadius: 6,
                flexWrap: "wrap",
              }}
            >
              <div
                aria-hidden="true"
                style={{
                  width: 88,
                  height: 88,
                  borderRadius: "50%",
                  background: "conic-gradient(var(--mk-primary) 0 90%, var(--mk-tint) 90% 100%)",
                  display: "grid",
                  placeItems: "center",
                  flex: "none",
                }}
              >
                <span
                  style={{
                    width: 74,
                    height: 74,
                    borderRadius: "50%",
                    background: "var(--mk-paper)",
                    display: "grid",
                    placeItems: "center",
                    /* v3.2 exception: mini-ring numeral scales with its 88px ring, not the page */
                    font: "500 28px var(--mk-mono)",
                    color: "var(--mk-primary)",
                  }}
                >
                  90
                </span>
              </div>
              <div className="mk-stack" style={{ gap: 4, minWidth: 0 }}>
                <span style={{ font: "600 var(--mk-text-h3-sm)/1.2 var(--mk-sans)" }}>Anonymized org · Fit score</span>
                <span style={{ font: "400 var(--mk-text-small)/1.4 var(--mk-sans)", color: "var(--mk-ink-2)" }}>
                  Boutique team · Austin, TX
                </span>
                <span style={{ font: "400 var(--mk-text-meta)/1.3 var(--mk-mono)", color: "var(--mk-muted)" }}>
                  42 agents · Mentorship
                </span>
              </div>
            </div>
            <p style={{ font: "400 var(--mk-text-body)/1.6 var(--mk-sans)", color: "var(--mk-ink-2)" }}>
              Product screenshots use anonymized org cards and the fit-score ring — never stock photography.
            </p>
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,340px),1fr))",
            gap: "32px clamp(40px,6cqi,96px)",
            paddingTop: 40,
            borderTop: "1px solid var(--mk-border)",
          }}
        >
          <div className="mk-stack" style={{ gap: 12 }}>
            <h3 style={{ font: "600 var(--mk-text-h3-sm)/1.2 var(--mk-sans)" }}>About Compaire</h3>
            <p className="mk-body" style={{ lineHeight: 1.65 }}>
              Compaire helps real estate agents get to know teams and brokerages before making a move. Agents can read
              company stories, follow updates, compare options with Fit Scores and reach out when they're ready. Teams
              and brokerages can explain their culture, training, compensation approach and support. Learn more at{" "}
              <a href="https://compaire.co" style={{ color: "var(--mk-primary)", fontWeight: 600 }}>
                compaire.co
              </a>
              .
            </p>
            <p className="mk-body" style={{ lineHeight: 1.65 }}>
              Category: Career Intelligence Platform
              <br />
              Founded: 2025 · Roseville, CA
              <br />
              Web:{" "}
              <a href="https://compaire.co" style={{ color: "var(--mk-primary)", fontWeight: 600 }}>
                compaire.co
              </a>
              <br />
              Press:{" "}
              <a href="mailto:press@compaire.co" style={{ color: "var(--mk-primary)", fontWeight: 600 }}>
                press@compaire.co
              </a>
            </p>
          </div>
          <div className="mk-stack" style={{ gap: 12 }}>
            <h3 style={{ font: "600 var(--mk-text-h3-sm)/1.2 var(--mk-sans)" }}>Press contact</h3>
            <p className="mk-body" style={{ lineHeight: 1.65 }}>
              Media inquiries —{" "}
              <a href="mailto:press@compaire.co" style={{ color: "var(--mk-primary)", fontWeight: 600 }}>
                press@compaire.co
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>

    <CtaBand
      eyebrow="Early access · 2026"
      gap={22}
      titleStyle={{ maxWidth: 860 }}
      title="Explore the career market most agents never see."
      body="Join the first group using Compaire to explore teams and brokerages at your own pace."
      cta={
        <SignUpCta variant="inverse" size="lg" style={{ padding: "0 26px" }}>
          Get started free →
        </SignUpCta>
      }
    />
  </MarketingLayout>
);

export default PressPage;
